/**
 * HealthContours pricing catalog.
 *
 * Same 5 levels for all 8 CareMap roles this pass — per-role price
 * differentiation is a future refinement. Checkout uses the existing
 * `createCheckoutSession` callable when a Stripe price id is synced into
 * Firestore `plans` (`stripe_metadata_id: "healthcontours"`). Paywalls are
 * not enforced (`canAccessDataset` remains a stub).
 */

export const HEALTHCONTOURS_STRIPE_METADATA_ID = "healthcontours";

/** Live MD3C prices (used until the Stripe extension syncs `plans`). */
export const HEALTHCONTOURS_STRIPE_PRICE_IDS = Object.freeze({
  single_use_report: "price_1U5yHSEQXF105V0ltdlBACWN",
  essential: "price_1U5yHTEQXF105V0lDVoUAY1y",
  professional: "price_1U5yHUEQXF105V0lOhxy8Tms",
});

export const HEALTHCONTOURS_TIER_IDS = Object.freeze([
  "free",
  "single_use_report",
  "essential",
  "professional",
  "enterprise",
]);

/** @typedef {'free' | 'single_use_report' | 'essential' | 'professional' | 'enterprise'} HealthContoursTierId */

/**
 * @typedef {object} HealthContoursTier
 * @property {HealthContoursTierId} id
 * @property {string} label
 * @property {string} priceLabel
 * @property {number | null} amountUsd
 * @property {'none' | 'payment' | 'subscription' | 'contact'} checkoutMode
 * @property {string} interval
 * @property {string} notes
 * @property {string} stripeTierKey
 */

/** @type {readonly HealthContoursTier[]} */
export const HEALTHCONTOURS_TIERS = Object.freeze([
  Object.freeze({
    id: "free",
    label: "Free",
    priceLabel: "$0",
    amountUsd: 0,
    checkoutMode: "none",
    interval: "none",
    notes: "Real synced data only (Facility Intelligence view).",
    stripeTierKey: "healthcontours_free",
  }),
  Object.freeze({
    id: "single_use_report",
    label: "Single-use report",
    priceLabel: "$329 one-time",
    amountUsd: 329,
    checkoutMode: "payment",
    interval: "one_time",
    notes: "One comparison / export unlock.",
    stripeTierKey: "healthcontours_single_report",
  }),
  Object.freeze({
    id: "essential",
    label: "Essential",
    priceLabel: "$219 / month",
    amountUsd: 219,
    checkoutMode: "subscription",
    interval: "month",
    notes: "Full dashboard, unlimited comparisons.",
    stripeTierKey: "healthcontours_essential",
  }),
  Object.freeze({
    id: "professional",
    label: "Professional",
    priceLabel: "$599 / month",
    amountUsd: 599,
    checkoutMode: "subscription",
    interval: "month",
    notes: "Exports, saved comparisons, trend data.",
    stripeTierKey: "healthcontours_professional",
  }),
  Object.freeze({
    id: "enterprise",
    label: "Enterprise / Team",
    priceLabel: "Starting at $47,000 / year",
    amountUsd: 47000,
    checkoutMode: "contact",
    interval: "year",
    notes: "Multi-seat, API access, custom reporting. Custom beyond this floor.",
    stripeTierKey: "healthcontours_enterprise",
  }),
]);

/**
 * HealthDesk `users.role` written at HealthContours sign-up.
 * MKT-26 / D-41-05: the client may create only a patient. `careMapRole` stays
 * the map persona and does not grant clinician apps.
 *
 * @param {string} _careMapRole
 * @returns {'patient'}
 */
export function healthDeskRoleForCareMapRole(_careMapRole) {
  return "patient";
}

/**
 * Pick a synced Stripe price for a HealthContours tier from `fetchActivePrices`
 * style rows (`product.stripe_metadata_id` + `product.healthcontours_tier` or
 * `price.nickname`).
 *
 * @param {Array<{ priceId: string, price: object, product: object }>} catalog
 * @param {HealthContoursTierId} tierId
 * @returns {{ priceId: string, price: object, product: object } | null}
 */
export function findHealthContoursPrice(catalog, tierId) {
  const tier = HEALTHCONTOURS_TIERS.find((row) => row.id === tierId);
  if (!tier) return null;
  const matched = Array.isArray(catalog)
    ? catalog.find((row) => {
        const product = row.product || {};
        const price = row.price || {};
        const metaId =
          product.stripe_metadata_id || product.metadata?.id || null;
        const tierKey =
          product.healthcontours_tier ||
          product.metadata?.healthcontours_tier ||
          price.nickname ||
          null;
        if (metaId !== HEALTHCONTOURS_STRIPE_METADATA_ID) return false;
        return tierKey === tier.stripeTierKey || tierKey === tier.id;
      }) || null
    : null;
  if (matched) return matched;
  const fallbackId = HEALTHCONTOURS_STRIPE_PRICE_IDS[tierId];
  if (!fallbackId) return null;
  return {
    priceId: fallbackId,
    price: {},
    product: {
      metadata: {
        id: HEALTHCONTOURS_STRIPE_METADATA_ID,
        healthcontours_tier: tier.stripeTierKey,
      },
    },
  };
}
